/**
 * SizingRenderer3D — 3D strategy for the sizing display object.
 *
 * Manages:
 * - Keypoint sphere actors (always visible)
 * - Keypoint label overlays (always visible)
 * - Current slice contour actor (always visible, follows currentNodeId)
 * - Selected measurement visualization:
 *   - Diameter: contour at the diameter's node + label
 *   - Length: tube path along centerline between keypoints + label
 *   - Angle: canvas overlay with two lines, arc, and label
 */

import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';
import vtkRenderer from '@kitware/vtk.js/Rendering/Core/Renderer';

import type { CenterlineNode } from '../../domain/centerline';
import { collectPathPoints } from '../../domain/sizing/computation/path-resolver';
import type { DiameterEdit } from '../../domain/sizing/diameter-edit';
import type { KeypointPlacement } from '../../domain/sizing/keypoint-placement';
import type { MeasurementResult } from '../../domain/sizing/measurement-result';
import type { Vec3 } from '../../domain/types';
import type { IViewport } from '../../viewports';
import { Path3DActor } from '../actors/path-3d-actor';
import { Sphere3DActor } from '../actors/sphere-3d-actor';
import { AngleOverlay } from '../overlay-actors/angle-overlay';
import { DiameterLabelOverlay } from '../overlay-actors/diameter-label-overlay';
import { KeypointOverlay } from '../overlay-actors/keypoint-overlay';
import { LengthLabelOverlay } from '../overlay-actors/length-label-overlay';
import type { SizingDisplayData, SizingDisplayProperties } from '../types';
import type { ISizingRenderer } from './sizing-display-object';

// ============================================================================
// Constants
// ============================================================================

const KEYPOINT_COLOR: [number, number, number] = [0.07, 0.4, 0.9];
const KEYPOINT_RADIUS = 3;
const CONTOUR_COLOR: [number, number, number] = [0.07, 0.4, 0.9];
const CONTOUR_RADIUS = 0.5;
const SELECTED_CONTOUR_COLOR: [number, number, number] = [0, 0.6, 0.9];
const LENGTH_PATH_COLOR: [number, number, number] = [0.2, 0.9, 0.2];
const LENGTH_PATH_RADIUS = 1.1;
const DRAFT_MARKER_COLOR: [number, number, number] = [1, 0.85, 0.2];
const DRAFT_MARKER_RADIUS = 2.5;

// Live preview (next point = current location, before validation)
const PREVIEW_COLOR: [number, number, number] = [1, 1, 1];
const PREVIEW_TUBE_RADIUS = LENGTH_PATH_RADIUS;
const PREVIEW_ANGLE_COLOR = 'rgba(255, 255, 255, 1.0)';

// ============================================================================
// SizingRenderer3D
// ============================================================================

export class SizingRenderer3D implements ISizingRenderer {
    private viewport: IViewport | null = null;
    private renderer: vtkRenderer | null = null;

    // Keypoint sphere actors (always visible)
    private keypointActors: Map<string, Sphere3DActor> = new Map();
    private keypointOverlays: Map<string, KeypointOverlay> = new Map();

    // Ghost keypoint sphere actors (no labels)
    private ghostKeypointActors: Map<string, Sphere3DActor> = new Map();

    // Draft point markers (no trace between them until committed)
    private draftSpheres: Sphere3DActor[] = [];

    // Live preview of the pending point (current location) + provisional trace
    private previewSphere: Sphere3DActor | null = null;
    private previewTubeActor: Path3DActor | null = null;
    private previewAngleOverlay: AngleOverlay | null = null;
    /** Fingerprint of the recreated preview tube to avoid rebuilding it every frame. */
    private previewTubeFingerprint: string | null = null;

    // Current contour (always visible, follows currentNodeId)
    private currentContourActor: Path3DActor | null = null;
    private currentContourNodeId: number | null = null;

    // Selected measurement actors (only one at a time)
    private selectedContourActor: Path3DActor | null = null;
    private selectedLengthActor: Path3DActor | null = null;
    private selectedDiameterLabelOverlay: DiameterLabelOverlay | null = null;
    private selectedLengthLabelOverlay: LengthLabelOverlay | null = null;
    private selectedAngleOverlay: AngleOverlay | null = null;
    private selectedMeasurementId: string | null = null;
    /** Fingerprint of the selected measurement to detect content changes without recreating actors. */
    private selectedMeasurementFingerprint: string | null = null;

    initialize(viewport: IViewport, _featureId: string): void {
        this.viewport = viewport;
        this.renderer = viewport.getRenderer(1);
    }

    update(data: SizingDisplayData, _properties: SizingDisplayProperties): void {
        if (!this.viewport || !this.renderer) return;

        this.updateKeypointSpheres(data.placements);
        this.updateKeypointLabels(data.placements);
        this.updateGhostKeypointSpheres(data.ghostPlacements);
        this.updateCurrentContour(data.currentNodeId, data.nodePool);
        this.updateDraft(data.draft);
        this.updatePreview(data.draft);
        this.updateSelectedMeasurement(data);
    }

    dispose(): void {
        this.removeAllKeypointActors();
        this.removeAllKeypointLabels();
        this.removeAllGhostKeypointActors();
        this.removeCurrentContour();
        this.removeAllDraftSpheres();
        this.removePreview();
        this.removeSelectedMeasurement();
        this.renderer = null;
        this.viewport = null;
    }

    getActors(): vtkActor[] {
        const actors: vtkActor[] = [];
        for (const sphere of this.keypointActors.values()) {
            actors.push(sphere.actor);
        }
        for (const sphere of this.ghostKeypointActors.values()) {
            actors.push(sphere.actor);
        }
        for (const sphere of this.draftSpheres) {
            actors.push(sphere.actor);
        }
        if (this.currentContourActor?.actor) actors.push(this.currentContourActor.actor);
        if (this.selectedContourActor?.actor) actors.push(this.selectedContourActor.actor);
        if (this.selectedLengthActor?.actor) actors.push(this.selectedLengthActor.actor);
        return actors;
    }

    // ========================================================================
    // Keypoint spheres
    // ========================================================================

    private updateKeypointSpheres(placements: ReadonlyMap<string, KeypointPlacement>): void {
        if (!this.renderer) return;
        const activeIds = new Set<string>();

        for (const [keypointId, placement] of placements) {
            activeIds.add(keypointId);
            const existing = this.keypointActors.get(keypointId);

            if (existing) {
                existing.setPosition(placement.worldPosition);
            } else {
                const sphere = new Sphere3DActor();
                sphere.setRadius(KEYPOINT_RADIUS);
                sphere.setColor(KEYPOINT_COLOR);
                sphere.setPosition(placement.worldPosition);
                sphere.setOpacity(1.0);
                this.renderer.addActor(sphere.actor);
                this.keypointActors.set(keypointId, sphere);
            }
        }

        // Remove stale spheres
        for (const [id, sphere] of this.keypointActors) {
            if (!activeIds.has(id)) {
                this.renderer.removeActor(sphere.actor);
                sphere.dispose();
                this.keypointActors.delete(id);
            }
        }
    }

    private removeAllKeypointActors(): void {
        if (!this.renderer) return;
        for (const [, sphere] of this.keypointActors) {
            this.renderer.removeActor(sphere.actor);
            sphere.dispose();
        }
        this.keypointActors.clear();
    }

    // ========================================================================
    // Ghost keypoint spheres (no labels)
    // ========================================================================

    private updateGhostKeypointSpheres(
        ghostPlacements: ReadonlyMap<string, readonly KeypointPlacement[]>
    ): void {
        if (!this.renderer) return;
        const activeIds = new Set<string>();

        for (const [keypointId, ghosts] of ghostPlacements) {
            for (let i = 0; i < ghosts.length; i++) {
                const ghostId = `${keypointId}-ghost-${i}`;
                activeIds.add(ghostId);
                const existing = this.ghostKeypointActors.get(ghostId);

                if (existing) {
                    existing.setPosition(ghosts[i].worldPosition);
                } else {
                    const sphere = new Sphere3DActor();
                    sphere.setRadius(KEYPOINT_RADIUS);
                    sphere.setColor(KEYPOINT_COLOR);
                    sphere.setPosition(ghosts[i].worldPosition);
                    this.renderer.addActor(sphere.actor);
                    this.ghostKeypointActors.set(ghostId, sphere);
                }
            }
        }

        // Remove stale ghost spheres
        for (const [id, sphere] of this.ghostKeypointActors) {
            if (!activeIds.has(id)) {
                this.renderer.removeActor(sphere.actor);
                sphere.dispose();
                this.ghostKeypointActors.delete(id);
            }
        }
    }

    private removeAllGhostKeypointActors(): void {
        for (const [, sphere] of this.ghostKeypointActors) {
            if (this.renderer) {
                this.renderer.removeActor(sphere.actor);
            }
            sphere.dispose();
        }
        this.ghostKeypointActors.clear();
    }

    // ========================================================================
    // Keypoint labels (canvas overlays tracking 3D positions)
    // ========================================================================

    private updateKeypointLabels(placements: ReadonlyMap<string, KeypointPlacement>): void {
        if (!this.viewport) return;
        const overlayLayer = this.viewport.getOverlayLayer();
        const activeIds = new Set<string>();

        for (const [keypointId, placement] of placements) {
            const overlayId = `sizing-3d-kp-${keypointId}`;
            activeIds.add(overlayId);

            const existing = this.keypointOverlays.get(overlayId);
            if (existing) {
                existing.setWorldPosition(placement.worldPosition);
            } else {
                const overlay = new KeypointOverlay({
                    id: overlayId,
                    label: keypointId,
                    worldPosition: placement.worldPosition,
                    color: 'rgb(18,102,230)',
                });
                this.keypointOverlays.set(overlayId, overlay);
                overlayLayer.addOverlayObject(overlay);
            }
        }

        // Remove stale
        for (const [id] of this.keypointOverlays) {
            if (!activeIds.has(id)) {
                overlayLayer.removeOverlayObject(id);
                this.keypointOverlays.delete(id);
            }
        }
    }

    private removeAllKeypointLabels(): void {
        if (!this.viewport) return;
        const overlayLayer = this.viewport.getOverlayLayer();
        for (const [id] of this.keypointOverlays) {
            overlayLayer.removeOverlayObject(id);
        }
        this.keypointOverlays.clear();
    }

    // ========================================================================
    // Current contour (follows currentNodeId)
    // ========================================================================

    private updateCurrentContour(
        currentNodeId: number | null,
        nodePool: ReadonlyMap<number, CenterlineNode>
    ): void {
        if (!this.renderer) return;

        if (currentNodeId === null) {
            this.removeCurrentContour();
            return;
        }

        // Only rebuild if node changed
        if (this.currentContourNodeId === currentNodeId && this.currentContourActor) return;

        this.removeCurrentContour();

        const node = nodePool.get(currentNodeId);
        if (!node || node.contour.length < 3) return;

        // Close the contour loop
        const closedContour: Vec3[] = [...node.contour, node.contour[0]];
        this.currentContourActor = new Path3DActor(closedContour, CONTOUR_RADIUS, CONTOUR_COLOR);
        this.currentContourNodeId = currentNodeId;

        if (this.currentContourActor.actor) {
            this.renderer.addActor(this.currentContourActor.actor);
        }
    }

    private removeCurrentContour(): void {
        if (this.renderer && this.currentContourActor?.actor) {
            this.renderer.removeActor(this.currentContourActor.actor);
        }
        this.currentContourActor?.dispose();
        this.currentContourActor = null;
        this.currentContourNodeId = null;
    }

    // ========================================================================
    // Draft point markers (markers only — no provisional trace)
    // ========================================================================

    private updateDraft(draft: SizingDisplayData['draft']): void {
        if (!this.renderer) return;

        const points = draft?.points ?? [];

        // Reconcile sphere count with the number of placed points
        while (this.draftSpheres.length > points.length) {
            const sphere = this.draftSpheres.pop()!;
            this.renderer.removeActor(sphere.actor);
            sphere.dispose();
        }
        while (this.draftSpheres.length < points.length) {
            const sphere = new Sphere3DActor();
            sphere.setRadius(DRAFT_MARKER_RADIUS);
            sphere.setColor(DRAFT_MARKER_COLOR);
            sphere.setOpacity(1.0);
            this.renderer.addActor(sphere.actor);
            this.draftSpheres.push(sphere);
        }

        for (let i = 0; i < points.length; i++) {
            this.draftSpheres[i].setPosition(points[i]);
        }
    }

    private removeAllDraftSpheres(): void {
        for (const sphere of this.draftSpheres) {
            if (this.renderer) this.renderer.removeActor(sphere.actor);
            sphere.dispose();
        }
        this.draftSpheres = [];
    }

    // ========================================================================
    // Live preview (next point = current location, white, before validation)
    // ========================================================================

    private updatePreview(draft: SizingDisplayData['draft']): void {
        if (!this.renderer || !this.viewport) return;

        const previewPoint = draft?.previewPoint ?? null;

        // No active preview (no draft, draft complete, or no current location).
        if (!draft || !previewPoint) {
            this.removePreview();
            return;
        }

        if (draft.kind === 'length') {
            // Length: white marker at the pending point + white tube along the
            // centerline. Tube hidden when the endpoints don't share a vessel path.
            this.removePreviewAngleOverlay();
            this.ensurePreviewSphere(previewPoint);
            this.syncPreviewTube(draft.previewPath ?? null);
            return;
        }

        // Angle: canvas overlay only — no 3D actors.
        this.removePreviewSphere();
        this.removePreviewTube();

        // Slot-resolved wedge: the vertex tracks its real slot regardless of
        // the order P1/Vertex/P2 were placed in. arm2 null ⇒ single-arm line.
        const angle = draft.previewAngle;
        if (angle) {
            this.syncPreviewAngle(angle.arm1, angle.vertex, angle.arm2);
        } else {
            this.removePreviewAngleOverlay();
        }
    }

    private ensurePreviewSphere(position: Vec3): void {
        if (!this.renderer) return;
        if (!this.previewSphere) {
            this.previewSphere = new Sphere3DActor();
            this.previewSphere.setRadius(DRAFT_MARKER_RADIUS);
            this.previewSphere.setColor(PREVIEW_COLOR);
            this.previewSphere.setOpacity(1.0);
            this.renderer.addActor(this.previewSphere.actor);
        }
        this.previewSphere.setPosition(position);
    }

    /** Recreate the preview tube only when its geometry changes. `path` null/short ⇒ no tube. */
    private syncPreviewTube(path: readonly Vec3[] | null): void {
        if (!this.renderer) return;

        const fingerprint =
            path && path.length >= 2
                ? `${path.length}:${vecKey(path[0])}:${vecKey(path[path.length - 1])}`
                : null;

        if (fingerprint === this.previewTubeFingerprint) return;

        this.removePreviewTube();
        this.previewTubeFingerprint = fingerprint;

        if (!path || path.length < 2) return;

        this.previewTubeActor = new Path3DActor([...path], PREVIEW_TUBE_RADIUS, PREVIEW_COLOR);
        if (this.previewTubeActor.actor) {
            this.renderer.addActor(this.previewTubeActor.actor);
        }
    }

    /** Update the preview angle overlay in place, creating it on first use. */
    private syncPreviewAngle(pointA: Vec3, vertex: Vec3, pointB: Vec3 | null): void {
        if (!this.viewport) return;

        if (this.previewAngleOverlay) {
            this.previewAngleOverlay.updateAngle(vertex, pointA, pointB, 0);
            return;
        }

        this.previewAngleOverlay = new AngleOverlay({
            id: 'sizing-3d-draft-angle',
            vertex,
            pointA,
            pointB,
            angleDegrees: 0,
            color: PREVIEW_ANGLE_COLOR,
            showLabel: false,
        });
        this.viewport.getOverlayLayer().addOverlayObject(this.previewAngleOverlay);
    }

    private removePreviewSphere(): void {
        if (this.renderer && this.previewSphere?.actor) {
            this.renderer.removeActor(this.previewSphere.actor);
        }
        this.previewSphere?.dispose();
        this.previewSphere = null;
    }

    private removePreviewTube(): void {
        if (this.renderer && this.previewTubeActor?.actor) {
            this.renderer.removeActor(this.previewTubeActor.actor);
        }
        this.previewTubeActor?.dispose();
        this.previewTubeActor = null;
        this.previewTubeFingerprint = null;
    }

    private removePreviewAngleOverlay(): void {
        if (this.viewport && this.previewAngleOverlay) {
            this.viewport.getOverlayLayer().removeOverlayObject(this.previewAngleOverlay.id);
        }
        this.previewAngleOverlay = null;
    }

    private removePreview(): void {
        this.removePreviewSphere();
        this.removePreviewTube();
        this.removePreviewAngleOverlay();
    }

    // ========================================================================
    // Selected measurement visualization
    // ========================================================================

    private updateSelectedMeasurement(data: SizingDisplayData): void {
        const { selectedMeasurementId } = data;

        if (!selectedMeasurementId) {
            if (this.selectedMeasurementId) this.removeSelectedMeasurement();
            return;
        }

        const measurement = data.measurements.get(selectedMeasurementId);
        if (!measurement) {
            if (this.selectedMeasurementId) this.removeSelectedMeasurement();
            return;
        }

        // Only recreate actors when the selection or measurement content has changed
        const fingerprint = `${selectedMeasurementId}:${measurement.navigationNodeId}:${measurement.value}`;
        if (fingerprint === this.selectedMeasurementFingerprint) return;

        this.removeSelectedMeasurement();
        this.selectedMeasurementId = selectedMeasurementId;
        this.selectedMeasurementFingerprint = fingerprint;

        const ctx = measurement.context;
        switch (ctx.type) {
            case 'diameter':
            case 'offset-diameter':
            case 'min-max-diameter':
            case 'custom-diameter':
                this.showDiameterContour(measurement, data);
                break;
            case 'length':
            case 'custom-length':
                this.showLengthPath(measurement, data);
                break;
            case 'angle':
            case 'custom-angle':
                this.showAngle(measurement, data);
                break;
        }
    }

    private showDiameterContour(measurement: MeasurementResult, data: SizingDisplayData): void {
        if (!this.renderer || !this.viewport) return;

        const nodeId = this.getDiameterNodeId(measurement, data.diameterEdits);
        if (nodeId === null) return;

        const node = data.nodePool.get(nodeId);
        if (!node || node.contour.length < 3) return;

        // Contour tube at the diameter's node
        const closedContour: Vec3[] = [...node.contour, node.contour[0]];
        this.selectedContourActor = new Path3DActor(closedContour, CONTOUR_RADIUS, SELECTED_CONTOUR_COLOR);
        if (this.selectedContourActor.actor) {
            this.renderer.addActor(this.selectedContourActor.actor);
        }

        // Diameter label with leader line from contour
        this.selectedDiameterLabelOverlay = new DiameterLabelOverlay({
            id: `sizing-3d-sel-label`,
            measurementName: measurement.shortLabel,
            value: measurement.value,
            contour: node.contour,
            color: 'rgb(0,159,227)',
        });
        this.viewport.getOverlayLayer().addOverlayObject(this.selectedDiameterLabelOverlay);
    }

    private showLengthPath(measurement: MeasurementResult, data: SizingDisplayData): void {
        if (!this.renderer || !this.viewport) return;

        const ctx = measurement.context;
        if (ctx.type !== 'length' && ctx.type !== 'custom-length') return;

        const pathPoints = collectPathPoints(ctx.fromNodeId, ctx.toNodeId, data.centerlines);
        if (pathPoints.length < 2) return;

        this.selectedLengthActor = new Path3DActor(pathPoints, LENGTH_PATH_RADIUS, LENGTH_PATH_COLOR);
        if (this.selectedLengthActor.actor) {
            this.renderer.addActor(this.selectedLengthActor.actor);
        }

        // Length label with leader lines to both endpoints
        this.selectedLengthLabelOverlay = new LengthLabelOverlay({
            id: `sizing-3d-sel-label`,
            measurementName: measurement.shortLabel,
            value: measurement.value,
            point1: pathPoints[0],
            point2: pathPoints[pathPoints.length - 1],
            color: 'rgb(0,169,147)',
        });
        this.viewport.getOverlayLayer().addOverlayObject(this.selectedLengthLabelOverlay);
    }

    private showAngle(measurement: MeasurementResult, data: SizingDisplayData): void {
        if (!this.viewport) return;

        const ctx = measurement.context;
        let vertex: Vec3;
        let pointA: Vec3;
        let pointB: Vec3;

        if (ctx.type === 'angle') {
            const a = data.placements.get(ctx.keypointIds[0]);
            const v = data.placements.get(ctx.keypointIds[1]);
            const b = data.placements.get(ctx.keypointIds[2]);
            if (!a || !v || !b) return;
            pointA = a.worldPosition;
            vertex = v.worldPosition;
            pointB = b.worldPosition;
        } else if (ctx.type === 'custom-angle') {
            const a = data.nodePool.get(ctx.nodeIds[0]);
            const v = data.nodePool.get(ctx.nodeIds[1]);
            const b = data.nodePool.get(ctx.nodeIds[2]);
            if (!a || !v || !b) return;
            pointA = a.point;
            vertex = v.point;
            pointB = b.point;
        } else {
            return;
        }

        this.selectedAngleOverlay = new AngleOverlay({
            id: `sizing-3d-sel-angle`,
            vertex,
            pointA,
            pointB,
            angleDegrees: measurement.value,
            measurementName: measurement.shortLabel,
            color: 'rgba(255, 255, 0, 1.0)',
        });
        this.viewport.getOverlayLayer().addOverlayObject(this.selectedAngleOverlay);
    }

    private removeSelectedMeasurement(): void {
        if (this.renderer) {
            if (this.selectedContourActor?.actor) {
                this.renderer.removeActor(this.selectedContourActor.actor);
            }
            if (this.selectedLengthActor?.actor) {
                this.renderer.removeActor(this.selectedLengthActor.actor);
            }
        }
        this.selectedContourActor?.dispose();
        this.selectedContourActor = null;
        this.selectedLengthActor?.dispose();
        this.selectedLengthActor = null;

        if (this.viewport) {
            const overlayLayer = this.viewport.getOverlayLayer();
            if (this.selectedDiameterLabelOverlay) {
                overlayLayer.removeOverlayObject(this.selectedDiameterLabelOverlay.id);
                this.selectedDiameterLabelOverlay = null;
            }
            if (this.selectedLengthLabelOverlay) {
                overlayLayer.removeOverlayObject(this.selectedLengthLabelOverlay.id);
                this.selectedLengthLabelOverlay = null;
            }
            if (this.selectedAngleOverlay) {
                overlayLayer.removeOverlayObject(this.selectedAngleOverlay.id);
                this.selectedAngleOverlay = null;
            }
        }

        this.selectedMeasurementId = null;
        this.selectedMeasurementFingerprint = null;
    }

    // ========================================================================
    // Helpers
    // ========================================================================

    private getDiameterNodeId(
        measurement: MeasurementResult,
        _diameterEdits: ReadonlyMap<number, DiameterEdit>
    ): number | null {
        const ctx = measurement.context;
        if (
            ctx.type === 'diameter' ||
            ctx.type === 'offset-diameter' ||
            ctx.type === 'min-max-diameter' ||
            ctx.type === 'custom-diameter'
        ) {
            return ctx.nodeId;
        }
        return null;
    }
}

/** Compact string key for a point, used to detect preview-geometry changes. */
function vecKey(p: Vec3): string {
    return `${p[0].toFixed(2)},${p[1].toFixed(2)},${p[2].toFixed(2)}`;
}
